import React, { useState, useEffect } from 'react';
import { ModernNavigation } from '../components/modern/ModernNavigation';
import { ModernFooter } from '../components/modern/ModernFooter';
import '../css/modern-design-system.css';
import '../css/modern-privacy.css';

export function ModernTermsOfService() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      const sections = document.querySelectorAll('.privacy-section');
      const scrollPosition = window.scrollY + 200;

      sections.forEach(section => {
        const top = (section as HTMLElement).offsetTop;
        const height = (section as HTMLElement).offsetHeight;
        const id = section.getAttribute('id');

        if (scrollPosition >= top && scrollPosition < top + height && id) {
          setActiveSection(id);
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="modern-privacy-page">
      <ModernNavigation scrolled={scrolled} />

      {/* Hero Section */}
      <section className="privacy-hero">
        <div className="privacy-hero-bg"></div>
        <div className="container">
          <div className="privacy-hero-content">
            <div className="privacy-badge">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
              <span>Legal Agreement</span>
            </div>
            <h1 className="privacy-hero-title">Terms of Service</h1>
            <p className="privacy-hero-subtitle">
              Last Updated: December 3, 2024
            </p>
            <div className="privacy-hero-meta">
              <span>Effective Date: December 3, 2024</span>
              <span>•</span>
              <span>Version 1.0</span>
            </div>
          </div>
        </div>
      </section>

      <div className="privacy-content-wrapper">
        <div className="container">
          <div className="privacy-layout">
            {/* Sidebar Navigation */}
            <aside className="privacy-sidebar">
              <div className="sidebar-sticky">
                <h3 className="sidebar-title">Table of Contents</h3>
                <nav className="privacy-nav">
                  <a
                    href="#acceptance"
                    className={`nav-item ${activeSection === 'acceptance' ? 'active' : ''}`}
                    onClick={(e) => { e.preventDefault(); scrollToSection('acceptance'); }}
                  >
                    Acceptance of Terms
                  </a>
                  <a
                    href="#eligibility"
                    className={`nav-item ${activeSection === 'eligibility' ? 'active' : ''}`}
                    onClick={(e) => { e.preventDefault(); scrollToSection('eligibility'); }}
                  >
                    Eligibility
                  </a>
                  <a
                    href="#account"
                    className={`nav-item ${activeSection === 'account' ? 'active' : ''}`}
                    onClick={(e) => { e.preventDefault(); scrollToSection('account'); }}
                  >
                    Account Registration
                  </a>
                  <a
                    href="#services"
                    className={`nav-item ${activeSection === 'services' ? 'active' : ''}`}
                    onClick={(e) => { e.preventDefault(); scrollToSection('services'); }}
                  >
                    Our Services
                  </a>
                  <a
                    href="#acceptable-use"
                    className={`nav-item ${activeSection === 'acceptable-use' ? 'active' : ''}`}
                    onClick={(e) => { e.preventDefault(); scrollToSection('acceptable-use'); }}
                  >
                    Acceptable Use
                  </a>
                  <a
                    href="#intellectual-property"
                    className={`nav-item ${activeSection === 'intellectual-property' ? 'active' : ''}`}
                    onClick={(e) => { e.preventDefault(); scrollToSection('intellectual-property'); }}
                  >
                    Intellectual Property
                  </a>
                  <a
                    href="#payments"
                    className={`nav-item ${activeSection === 'payments' ? 'active' : ''}`}
                    onClick={(e) => { e.preventDefault(); scrollToSection('payments'); }}
                  >
                    Payments & Subscriptions
                  </a>
                  <a
                    href="#disclaimers"
                    className={`nav-item ${activeSection === 'disclaimers' ? 'active' : ''}`}
                    onClick={(e) => { e.preventDefault(); scrollToSection('disclaimers'); }}
                  >
                    Disclaimers
                  </a>
                  <a
                    href="#liability"
                    className={`nav-item ${activeSection === 'liability' ? 'active' : ''}`}
                    onClick={(e) => { e.preventDefault(); scrollToSection('liability'); }}
                  >
                    Limitation of Liability
                  </a>
                  <a
                    href="#indemnification"
                    className={`nav-item ${activeSection === 'indemnification' ? 'active' : ''}`}
                    onClick={(e) => { e.preventDefault(); scrollToSection('indemnification'); }}
                  >
                    Indemnification
                  </a>
                  <a
                    href="#termination"
                    className={`nav-item ${activeSection === 'termination' ? 'active' : ''}`}
                    onClick={(e) => { e.preventDefault(); scrollToSection('termination'); }}
                  >
                    Termination
                  </a>
                  <a
                    href="#disputes"
                    className={`nav-item ${activeSection === 'disputes' ? 'active' : ''}`}
                    onClick={(e) => { e.preventDefault(); scrollToSection('disputes'); }}
                  >
                    Dispute Resolution
                  </a>
                  <a
                    href="#changes"
                    className={`nav-item ${activeSection === 'changes' ? 'active' : ''}`}
                    onClick={(e) => { e.preventDefault(); scrollToSection('changes'); }}
                  >
                    Changes to Terms
                  </a>
                  <a
                    href="#contact"
                    className={`nav-item ${activeSection === 'contact' ? 'active' : ''}`}
                    onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}
                  >
                    Contact Us
                  </a>
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <main className="privacy-main">
              <section id="acceptance" className="privacy-section">
                <h2>Acceptance of Terms</h2>
                <p>
                  Welcome to EquityWorkx. These Terms of Service ("Terms") govern your access to and use of
                  the EquityWorkx website, mobile applications, and services (collectively, the "Services").
                  By accessing or using our Services, you agree to be bound by these Terms and our Privacy Policy.
                </p>
                <p>
                  If you do not agree to these Terms, you may not access or use our Services. We recommend
                  that you print or save a copy of these Terms for your records.
                </p>

                <div className="highlight-box">
                  <h3>Important Notice</h3>
                  <p>
                    These Terms contain an arbitration agreement and class action waiver that affect your
                    legal rights. Please read them carefully before using our Services.
                  </p>
                </div>
              </section>

              <section id="eligibility" className="privacy-section">
                <h2>Eligibility</h2>
                <p>
                  To use our Services, you must:
                </p>
                <ul>
                  <li>Be at least 18 years of age</li>
                  <li>Be a legal resident of the United States or Canada</li>
                  <li>Have the legal capacity to enter into a binding agreement</li>
                  <li>Not be prohibited from using our Services under applicable laws</li>
                </ul>
                <p>
                  By using our Services, you represent and warrant that you meet all eligibility requirements.
                  If you are using our Services on behalf of an organization, you represent that you have the
                  authority to bind that organization to these Terms.
                </p>
              </section>

              <section id="account" className="privacy-section">
                <h2>Account Registration</h2>

                <h3>Creating an Account</h3>
                <p>
                  To access certain features of our Services, you must create an account. When registering,
                  you agree to:
                </p>
                <ul>
                  <li>Provide accurate, current, and complete information</li>
                  <li>Maintain and promptly update your account information</li>
                  <li>Keep your login credentials confidential and secure</li>
                  <li>Notify us immediately of any unauthorized access to your account</li>
                  <li>Accept responsibility for all activities under your account</li>
                </ul>

                <h3>Account Security</h3>
                <p>
                  You are responsible for maintaining the security of your account credentials. We recommend
                  using a strong, unique password and enabling multi-factor authentication when available.
                  EquityWorkx will never ask for your password via email or phone.
                </p>
              </section>

              <section id="services" className="privacy-section">
                <h2>Our Services</h2>

                <h3>Description of Services</h3>
                <p>
                  EquityWorkx provides financial planning tools and insights designed to help homeowners
                  optimize their mortgage and build wealth. Our Services include:
                </p>
                <ul>
                  <li><strong>Mortgage Tracking:</strong> Tools to monitor your mortgage balance, payments, and equity growth</li>
                  <li><strong>Financial Insights:</strong> Personalized recommendations based on your financial situation</li>
                  <li><strong>Wealth Calculators:</strong> Interactive tools to model various financial scenarios</li>
                  <li><strong>Refinance Assessments:</strong> AI-powered analysis of refinancing opportunities</li>
                  <li><strong>Agent Connections:</strong> Introductions to qualified financial professionals</li>
                </ul>

                <h3>Third-Party Integrations</h3>
                <p>
                  Our Services may integrate with third-party services such as Plaid for financial account
                  connectivity. Your use of these integrations is subject to the respective third party's
                  terms of service and privacy policies.
                </p>

                <div className="highlight-box">
                  <h4>Not Financial Advice</h4>
                  <p>
                    EquityWorkx provides informational tools and insights only. Our Services do not constitute
                    financial, investment, tax, or legal advice. Always consult with qualified professionals
                    before making financial decisions.
                  </p>
                </div>
              </section>

              <section id="acceptable-use" className="privacy-section">
                <h2>Acceptable Use</h2>

                <p>You agree to use our Services only for lawful purposes and in accordance with these Terms. You agree NOT to:</p>

                <ul>
                  <li>Use the Services for any illegal or unauthorized purpose</li>
                  <li>Violate any applicable laws, regulations, or third-party rights</li>
                  <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
                  <li>Interfere with or disrupt the integrity or performance of the Services</li>
                  <li>Transmit any viruses, malware, or other harmful code</li>
                  <li>Use automated systems (bots, scrapers, etc.) without our permission</li>
                  <li>Reverse engineer, decompile, or disassemble any part of the Services</li>
                  <li>Remove or alter any proprietary notices or labels</li>
                  <li>Use the Services to compete with EquityWorkx</li>
                  <li>Provide false or misleading information</li>
                  <li>Harass, abuse, or harm other users</li>
                </ul>

                <p>
                  We reserve the right to investigate and take appropriate action against anyone who violates
                  these provisions, including removing content, suspending accounts, and reporting to law enforcement.
                </p>
              </section>

              <section id="intellectual-property" className="privacy-section">
                <h2>Intellectual Property</h2>

                <h3>Our Intellectual Property</h3>
                <p>
                  The Services and all content, features, and functionality (including but not limited to
                  text, graphics, logos, icons, images, audio, video, software, and data) are owned by
                  EquityWorkx, our licensors, or other content providers and are protected by copyright,
                  trademark, patent, and other intellectual property laws.
                </p>

                <h3>Limited License</h3>
                <p>
                  We grant you a limited, non-exclusive, non-transferable, revocable license to access and
                  use our Services for your personal, non-commercial use. This license does not include the
                  right to:
                </p>
                <ul>
                  <li>Modify or copy the Services or any content</li>
                  <li>Use the Services for any commercial purpose</li>
                  <li>Transfer the license to any third party</li>
                  <li>Use any data mining, robots, or similar data gathering methods</li>
                </ul>

                <h3>Your Content</h3>
                <p>
                  You retain ownership of any content you submit through our Services. By submitting content,
                  you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify,
                  and display such content in connection with providing our Services.
                </p>
              </section>

              <section id="payments" className="privacy-section">
                <h2>Payments & Subscriptions</h2>

                <h3>Free and Paid Services</h3>
                <p>
                  EquityWorkx offers both free and paid subscription services. Certain features require a
                  paid subscription. Subscription fees and features are described on our website and apps.
                </p>

                <h3>Billing</h3>
                <p>
                  For paid subscriptions:
                </p>
                <ul>
                  <li>You authorize us to charge your payment method for all fees when due</li>
                  <li>Subscriptions automatically renew unless cancelled before the renewal date</li>
                  <li>Prices may change with notice; continued use constitutes acceptance</li>
                  <li>You are responsible for all applicable taxes</li>
                </ul>

                <h3>Refunds</h3>
                <p>
                  Refund policies vary by platform (App Store, Google Play, web). Generally, subscription
                  fees are non-refundable except as required by law or as described in our refund policy.
                </p>

                <h3>Cancellation</h3>
                <p>
                  You may cancel your subscription at any time. Cancellation takes effect at the end of the
                  current billing period. You will retain access to paid features until then.
                </p>
              </section>

              <section id="disclaimers" className="privacy-section">
                <h2>Disclaimers</h2>

                <div className="highlight-box">
                  <p>
                    <strong>THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY
                    KIND, EITHER EXPRESS OR IMPLIED.</strong>
                  </p>
                </div>

                <p>To the fullest extent permitted by law, EquityWorkx disclaims all warranties, including but not limited to:</p>
                <ul>
                  <li>Implied warranties of merchantability, fitness for a particular purpose, and non-infringement</li>
                  <li>Warranties that the Services will be uninterrupted, error-free, or secure</li>
                  <li>Warranties regarding the accuracy, reliability, or completeness of any information</li>
                  <li>Warranties that defects will be corrected</li>
                </ul>

                <h3>Financial Disclaimer</h3>
                <p>
                  EquityWorkx is not a financial advisor, mortgage broker, or lender. The insights and
                  recommendations provided through our Services are for informational purposes only and
                  should not be relied upon as financial advice. Market conditions, interest rates, and
                  individual circumstances vary. Always conduct your own research and consult with qualified
                  professionals before making financial decisions.
                </p>
              </section>

              <section id="liability" className="privacy-section">
                <h2>Limitation of Liability</h2>

                <p>
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, EQUITYWORKX AND ITS OFFICERS, DIRECTORS, EMPLOYEES,
                  AGENTS, AND LICENSORS SHALL NOT BE LIABLE FOR:
                </p>
                <ul>
                  <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                  <li>Loss of profits, revenue, data, or business opportunities</li>
                  <li>Damages resulting from your use of or inability to use the Services</li>
                  <li>Damages resulting from any third-party content or services</li>
                  <li>Damages exceeding the amount you paid us in the 12 months preceding the claim</li>
                </ul>

                <p>
                  Some jurisdictions do not allow the exclusion or limitation of certain damages, so some
                  of the above limitations may not apply to you.
                </p>
              </section>

              <section id="indemnification" className="privacy-section">
                <h2>Indemnification</h2>

                <p>
                  You agree to indemnify, defend, and hold harmless EquityWorkx and its officers, directors,
                  employees, agents, licensors, and service providers from and against any claims, liabilities,
                  damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys'
                  fees) arising out of or relating to:
                </p>
                <ul>
                  <li>Your violation of these Terms</li>
                  <li>Your use of the Services</li>
                  <li>Your violation of any third-party rights</li>
                  <li>Any content you submit through the Services</li>
                </ul>
              </section>

              <section id="termination" className="privacy-section">
                <h2>Termination</h2>

                <h3>Termination by You</h3>
                <p>
                  You may terminate your account at any time by contacting us or using the account deletion
                  feature in the app. Upon termination, your right to use the Services will immediately cease.
                </p>

                <h3>Termination by Us</h3>
                <p>
                  We may suspend or terminate your account and access to the Services at any time, with or
                  without cause, with or without notice. Reasons for termination may include:
                </p>
                <ul>
                  <li>Violation of these Terms</li>
                  <li>Fraudulent, abusive, or illegal activity</li>
                  <li>Non-payment of fees</li>
                  <li>Extended periods of inactivity</li>
                  <li>Requests by law enforcement</li>
                </ul>

                <h3>Effect of Termination</h3>
                <p>
                  Upon termination, your license to use the Services terminates. Sections of these Terms that
                  by their nature should survive termination will survive, including intellectual property
                  provisions, disclaimers, limitations of liability, and dispute resolution.
                </p>
              </section>

              <section id="disputes" className="privacy-section">
                <h2>Dispute Resolution</h2>

                <h3>Informal Resolution</h3>
                <p>
                  Before filing a claim, you agree to try to resolve the dispute informally by contacting us
                  at legal@equityworkx.com. We will try to resolve the dispute within 60 days.
                </p>

                <h3>Arbitration Agreement</h3>
                <p>
                  If we cannot resolve the dispute informally, you and EquityWorkx agree to resolve any
                  disputes through binding arbitration, rather than in court. Arbitration will be conducted
                  by the American Arbitration Association (AAA) under its Consumer Arbitration Rules.
                </p>

                <h3>Class Action Waiver</h3>
                <p>
                  YOU AND EQUITYWORKX AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS
                  INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR
                  REPRESENTATIVE PROCEEDING.
                </p>

                <h3>Governing Law</h3>
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of the State
                  of Delaware, without regard to its conflict of law provisions.
                </p>
              </section>

              <section id="changes" className="privacy-section">
                <h2>Changes to Terms</h2>

                <p>
                  We may update these Terms from time to time. When we make changes, we will:
                </p>
                <ul>
                  <li>Update the "Last Updated" date at the top of these Terms</li>
                  <li>Notify you of material changes via email or in-app notification</li>
                  <li>Post the updated Terms on our website</li>
                </ul>
                <p>
                  Your continued use of the Services after changes become effective constitutes your
                  acceptance of the revised Terms. If you do not agree to the updated Terms, you must
                  stop using our Services.
                </p>
              </section>

              <section id="contact" className="privacy-section">
                <h2>Contact Us</h2>

                <p>If you have questions about these Terms, please contact us:</p>

                <div className="contact-info">
                  <div className="contact-method">
                    <h4>Email</h4>
                    <a href="mailto:legal@equityworkx.com">legal@equityworkx.com</a>
                  </div>
                </div>
              </section>
            </main>
          </div>
        </div>
      </div>

      <ModernFooter />
    </div>
  );
}
